import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAppServices } from "../../../../hook/services";
import { useUserInfo } from "../../../../context/user";
import CreateWebhookModal from "../../../../components/CreateWebhookModal";


const WebhookGlyph = ({ size = 24 }) => (
  <img src="https://event-bridge-spark.lovable.app/assets/webhook-icon-CoSIHORZ.png" alt="Webhook" width={size} height={size} />
);

const EyeIcon = ({ size = 18 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOffIcon = ({ size = 18 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
    <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
    <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
    <line x1="2" y1="2" x2="22" y2="22" />
  </svg>
);

const CopyIcon = ({ size = 18 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
    <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
  </svg>
);

const TrashIcon = ({ size = 18 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 6h18" />
    <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
    <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
    <line x1="10" y1="11" x2="10" y2="17" />
    <line x1="14" y1="11" x2="14" y2="17" />
  </svg>
);

const CheckIcon = ({ size = 18 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const CloseIcon = ({ size = 18 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);


const formatCreatedDate = (dateInput) => {
  if (!dateInput) return "";
  const date = new Date(dateInput);
  if (isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const Toggle = ({ checked, onChange, disabled }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={() => !disabled && onChange(!checked)}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${checked ? "bg-black" : "bg-[#d1d5db]"
      } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
  >
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${checked ? "translate-x-6" : "translate-x-1"
        }`}
    />
  </button>
);

const CopyButton = ({ value }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value || "");
    } catch (error) {
      // Clipboard may be unavailable; ignore silently.
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      aria-label="Copy"
      className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md border !border-[#D9D9D9] bg-white hover:bg-[#f8fafc] ${copied ? " text-[#16a34a]" : " text-[#000000]"
        }`}
    >
      {copied ? <CheckIcon /> : <CopyIcon />}
    </button>
  );
};


function Webhooks() {
  const Service = useAppServices();
  const userInfo = useUserInfo();
  const user = userInfo[0];
  const userActiveLocation = userInfo[4];
  const user_id = user?._id;
  const account_id = userActiveLocation?.id;

  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);

  const [detailsId, setDetailsId] = useState(null);
  const [detailsData, setDetailsData] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [showSecret, setShowSecret] = useState(false);

  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [togglingId, setTogglingId] = useState(null);

  const detailsWebhook = useMemo(
    () => webhooks.find((hook) => hook.source_id === detailsId) || null,
    [webhooks, detailsId]
  );
  const deleteWebhook = useMemo(
    () => webhooks.find((hook) => hook.source_id === deleteId) || null,
    [webhooks, deleteId]
  );

  const loadWebhooks = useCallback(async () => {
    if (!user_id) return;
    setLoading(true);
    setErrorMsg("");
    const query = account_id
      ? `user_id=${user_id}&account_id=${account_id}`
      : `user_id=${user_id}`;
    const { response, error } = await Service.webhook_config.list({ query });
    if (error) {
      setErrorMsg(error?.message || "Failed to load webhooks");
      setLoading(false);
      return;
    }
    setWebhooks(response?.data?.webhooks || []);
    setLoading(false);
  }, [user_id, account_id]);

  useEffect(() => {
    loadWebhooks();
  }, [loadWebhooks]);

  const closeCreate = () => {
    setShowCreate(false);
    setNewName("");
  };

  const handleCreate = async () => {
    const trimmed = newName.trim();
    if (!trimmed || !user_id) return;
    setCreating(true);
    const { response, error } = await Service.webhook_config.create({
      payload: {
        user_id,
        account_id,
        webhook_name: trimmed,
      },
    });
    setCreating(false);
    if (error) {
      setErrorMsg(error?.message || "Failed to create webhook");
      return;
    }
    const created = response?.data;
    if (created) {
      setWebhooks((current) => [
        {
          source_id: created.source_id,
          webhook_name: created.webhook_name,
          active: created.active,
          configured_at: created.configured_at,
        },
        ...current,
      ]);
      setDetailsData({
        source_id: created.source_id,
        webhook_name: created.webhook_name,
        source_url: created.source_url,
        secret_key: created.secret_key,
        active: created.active,
      });
      setDetailsId(created.source_id);
      setShowSecret(false);
    }
    closeCreate();
  };

  const handleToggleActive = async (source_id, value) => {
    if (!user_id) return;
    setTogglingId(source_id);
    const { error } = await Service.webhook_config.toggle({
      source_id,
      payload: { user_id, active: value },
    });
    setTogglingId(null);
    if (error) {
      setErrorMsg(error?.message || "Failed to toggle webhook");
      return;
    }
    setWebhooks((current) =>
      current.map((hook) =>
        hook.source_id === source_id ? { ...hook, active: value } : hook
      )
    );
  };

  const openDetails = async (source_id) => {
    if (!user_id) return;
    setShowSecret(false);
    setDetailsId(source_id);
    setDetailsData(null);
    setDetailsLoading(true);
    const { response, error } = await Service.webhook_config.get_secret({
      source_id,
      query: `user_id=${user_id}`,
    });
    setDetailsLoading(false);
    if (error) {
      setErrorMsg(error?.message || "Failed to load webhook details");
      setDetailsId(null);
      return;
    }
    setDetailsData(response?.data || null);
  };

  const closeDetails = () => {
    setDetailsId(null);
    setDetailsData(null);
    setShowSecret(false);
  };

  const confirmDelete = async () => {
    if (!deleteId || !user_id) return;
    setDeleting(true);
    const { error } = await Service.webhook_config.delete({
      source_id: deleteId,
      query: `user_id=${user_id}`,
    });
    setDeleting(false);
    if (error) {
      const linkedTasks = error?.data?.count;
      if (error?.code === "TASKS_LINKED" && linkedTasks) {
        setErrorMsg(
          `Cannot delete: ${linkedTasks} task(s) are using this webhook. Remove the tasks first.`
        );
      } else {
        setErrorMsg(error?.message || "Failed to delete webhook");
      }
      return;
    }
    setWebhooks((current) => current.filter((hook) => hook.source_id !== deleteId));
    setDeleteId(null);
  };

  return (
    <div className="">
      <div className="pb-6 mb-6 flex items-start justify-between gap-4">
        <div>
          <h2 style={{ fontSize: "28px", fontWeight: "bold" }}>Webhooks</h2>
          <p className="pt-2 text-gray-700">
            Webhooks let external tools send events into your account.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowCreate(true)}
          className="flex flex-shrink-0 items-center gap-2 rounded-md bg-black px-4 h-11 text-[14px] font-medium text-white  whitespace-nowrap"
        >
          <span className="text-[18px] leading-none">+</span>
          Create webhook
        </button>
      </div>

      {errorMsg && (
        <div className="mb-4 rounded-md border border-[#fecaca] bg-[#fef2f2] text-[#b91c1c] text-[14px] px-4 py-3 flex items-start justify-between gap-3">
          <span>{errorMsg}</span>
          <button
            type="button"
            onClick={() => setErrorMsg("")}
            className="text-[#b91c1c]/70 hover:text-[#b91c1c]"
            aria-label="Dismiss"
          >
            <CloseIcon size={16} />
          </button>
        </div>
      )}

      {/* List */}
      <div className="flex flex-col gap-3">
        {loading ? (
          <div className="text-center text-[14px] text-[#64748b] py-16 border border-dashed !border-[#D9D9D9] rounded-lg">
            Loading webhooks...
          </div>
        ) : (
          <>
            {webhooks.map((hook) => (
              <div
                key={hook.source_id}
                className="flex items-center justify-between gap-4 rounded-lg border !border-[#D9D9D9] bg-white px-4 py-4"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-md bg-[#f0f0f0]">
                    <WebhookGlyph />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[15px] font-semibold text-[#0f172a] truncate">
                      {hook.webhook_name}
                    </div>
                    <div className="text-[13px] text-[#757575]">
                      Created {formatCreatedDate(hook.configured_at)}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3 flex-shrink-0">
                  <span
                    className={`text-[14px] ${hook.active ? "text-[#0f172a]" : "text-[#757575]"
                      }`}
                  >
                    {hook.active ? "Active" : "Inactive"}
                  </span>
                  <Toggle
                    checked={hook.active}
                    disabled={togglingId === hook.source_id}
                    onChange={(value) => handleToggleActive(hook.source_id, value)}
                  />
                  <button
                    type="button"
                    onClick={() => openDetails(hook.source_id)}
                    className="flex items-center gap-2 rounded-md border !border-[#D9D9D9] bg-white px-4 h-10 text-[14px] font-medium text-[#0f172a] hover:bg-[#f8fafc]"
                  >
                    <EyeIcon size={16} />
                    View details
                  </button>
                  <button
                    type="button"
                    aria-label="Delete webhook"
                    onClick={() => setDeleteId(hook.source_id)}
                    className="flex h-10 w-10 items-center justify-center rounded-md text-[#757575] hover:bg-[#fef2f2] hover:text-[#dc2626]"
                  >
                    <TrashIcon />
                  </button>
                </div>
              </div>
            ))}

            {webhooks.length === 0 && (
              <div className="text-center text-[14px] text-[#64748b] py-16 border border-dashed !border-[#D9D9D9] rounded-lg">
                No webhooks yet. Create one to start receiving events.
              </div>
            )}
          </>
        )}
      </div>

      {/* Create webhook modal (shared with Tasks form) */}
      <CreateWebhookModal
        open={showCreate}
        name={newName}
        onNameChange={setNewName}
        creating={creating}
        onCreate={handleCreate}
        onClose={closeCreate}
      />

      {detailsId && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={closeDetails}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-[520px] max-w-[92vw] p-6"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 mb-1">
              <h3 className="text-[16px] font-semibold text-[#0f172a]">
                {detailsData?.webhook_name || detailsWebhook?.webhook_name || "Webhook"}
              </h3>
              <button
                type="button"
                aria-label="Close"
                onClick={closeDetails}
                className="text-[#757575] hover:text-black"
              >
                <CloseIcon />
              </button>
            </div>
            <p className="text-[13px] text-[#757575] mb-5">
              Use this URL  in the service that will send data to your
              account.
            </p>

            {detailsLoading ? (
              <div className="text-center text-[14px] text-[#64748b] py-8">
                Loading...
              </div>
            ) : (
              <>
                <label className="text-[13px] font-medium block mb-2">
                  Webhook URL
                </label>
                <div className="flex items-center gap-2 mb-5">
                  <input
                    readOnly
                    value={detailsData?.source_url || ""}
                    className="w-full h-10 border !border-[#D9D9D9] rounded-md px-3 text-[14px] bg-[#f1f5f9] text-[#0f172a] outline-none font-mono"
                  />
                  <CopyButton value={detailsData?.source_url} />
                </div>
                {/* 
                <label className="text-[13px] font-medium block mb-2">
                  Secret key
                </label>
                <div className="flex items-center gap-2">
                  <div className="relative w-full">
                    <input
                      readOnly
                      type={showSecret ? "text" : "password"}
                      value={detailsData?.secret_key || ""}
                      placeholder={detailsData?.secret_key ? "" : "No secret (custom provider)"}
                      className="w-full h-10 border !border-[#D9D9D9] rounded-md pl-3 pr-10 text-[14px] bg-[#f1f5f9] text-[#0f172a] outline-none font-mono"
                    />
                    <button
                      type="button"
                      aria-label={showSecret ? "Hide secret" : "Show secret"}
                      onClick={() => setShowSecret((value) => !value)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-[#64748b] hover:text-black"
                    >
                      {showSecret ? <EyeOffIcon /> : <EyeIcon />}
                    </button>
                  </div>
                  <CopyButton value={detailsData?.secret_key} />
                </div> 
                <p className="text-[12px] text-[#757575] mt-2">
                  Keep this secret safe. We&apos;ll verify authenticity of the
                  incoming requests with it.
                </p>*/}
              </>
            )}

            <div className="flex items-center justify-end mt-6">
              <button
                type="button"
                onClick={closeDetails}
                className="h-11 px-5 rounded-md bg-black text-[14px] font-medium text-white hover:bg-gray-800"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteWebhook && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={() => !deleting && setDeleteId(null)}
        >
          <div
            className="bg-white rounded-md shadow-xl w-[520px] max-w-[92vw] p-6"
            onClick={(event) => event.stopPropagation()}
          >
            <h3 className="text-[16px] font-semibold text-[#0f172a] mb-2">
              Delete webhook?
            </h3>
            <p className="text-[13px] text-[#757575]">
              This will stop events from being sent to{" "}
              <span className="font-semibold text-[#0f172a]">
                &quot;{deleteWebhook.webhook_name}&quot;
              </span>
              . This action cannot be undone.
            </p>

            <div className="flex items-center justify-end gap-2 mt-6">
              <button
                type="button"
                onClick={() => setDeleteId(null)}
                disabled={deleting}
                className="h-11 px-4 rounded-md border !border-[#D9D9D9] text-[13px] text-[#000000] bg-white hover:bg-[#f8fafc] disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={deleting}
                className="h-11 px-5 rounded-md bg-[#C00F0C] text-[13px] font-medium text-white hover:bg-[#B91C1C] disabled:opacity-50"
              >
                {deleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Webhooks;
